import React, { useEffect, useState, useRef } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Slider
} from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import AuthHeader from "../components/AuthHeader";

function IrregularVerbsPage({ API_BASE, AUDIO_BASE }) {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [irregularVerbs, setIrregularVerbs] = useState([]);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);

  const audioRef = useRef(null);

  // ログイン状態チェック
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/irregular_verbs' }, replace: true });
    }
  }, [isAuthenticated, navigate]);

  // 不規則動詞データを取得
  useEffect(() => {
    const fetchIrregularVerbs = async () => {
      try {
        const response = await fetch(`${API_BASE}/irregular_verbs`);
        const data = await response.json();
        setIrregularVerbs(data);
      } catch (error) {
        console.error('不規則動詞データの取得に失敗:', error);
      }
    };

    fetchIrregularVerbs();
  }, [API_BASE]);

  // 再生速度変更時に音声プレイヤーにも反映
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // 音声の再生/停止
  const handlePlay = async (audioFileName, playId) => {
    if (!audioFileName) {
      console.error('音声ファイルが指定されていません');
      return;
    }

    // 同じボタンをクリックした場合は停止
    if (currentlyPlaying === playId) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setCurrentlyPlaying(null);
      return;
    }

    // 別の音声を再生する場合は、現在の音声を停止
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    // 新しい音声を設定して再生
    setCurrentlyPlaying(playId);

    // 音声ファイルのパスを設定
    const audioPath = `${AUDIO_BASE}/irregular_verbs_audio/${audioFileName}`;

    if (audioRef.current) {
      audioRef.current.src = audioPath;
      audioRef.current.playbackRate = playbackRate;
      try {
        await audioRef.current.play();
      }
      catch (error) {
        console.error('音声再生エラー:', error);
        setCurrentlyPlaying(null);
      }
    }
  };

  // 音声終了時の処理
  const handleAudioEnded = () => {
    setCurrentlyPlaying(null);
  };

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  // ログイン中のみ表示
  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <AuthHeader />
      <Container maxWidth="md" sx={{ paddingBottom: 14 }}>
        <Box display="flex" flexDirection="column" gap={3}>
          {irregularVerbs.map((verb) => (
            <Paper
              key={verb.id}
              sx={{
                padding: 2,
                backgroundColor: currentlyPlaying === verb.id ? "#e3f2fd" : "inherit",
              }}
            >
              <Box sx={{ display: 'flex', gap: 4, marginBottom: 1, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">原形</Typography>
                  <Typography variant="h5">{verb.base_form}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">過去形</Typography>
                  <Typography variant="h5">{verb.past_form}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">過去分詞</Typography>
                  <Typography variant="h5">{verb.past_participle}</Typography>
                </Box>
              </Box>

              <Typography variant="h6" color="text.secondary" sx={{ marginBottom: 1 }}>
                {verb.meaning}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {currentlyPlaying === verb.id ? (
                  <Button variant="outlined" color="error" onClick={() => handlePlay(verb.audio_file, verb.id)} sx={{ mr: 1 }}>
                    停止
                  </Button>
                ) : (
                  <Button variant="contained" onClick={() => handlePlay(verb.audio_file, verb.id)} sx={{ mr: 1 }}>
                    再生
                  </Button>
                )}
              </Box>
            </Paper>
          ))}
        </Box>

        {/* 音声プレイヤー（非表示） */}
        <audio
          ref={audioRef}
          onEnded={handleAudioEnded}
          style={{ display: 'none' }}
          preload="auto"
        />

        {/* 再生速度コントロール */}
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100%",
            backgroundColor: "#fff",
            borderTop: "1px solid #ccc",
            padding: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <Box minWidth={200}>
            <Typography gutterBottom>再生速度：{playbackRate.toFixed(1)}x</Typography>
            <Slider
              value={playbackRate}
              onChange={(_, newValue) => setPlaybackRate(newValue)}
              min={0.5}
              max={1.5}
              step={0.1}
              valueLabelDisplay="auto"
              marks={[
                { value: 0.5, label: "0.5x" },
                { value: 1.0, label: "1.0x" },
                { value: 1.5, label: "1.5x" }
              ]}
            />
          </Box>
        </Box>
      </Container>
    </>
  );
}

export default IrregularVerbsPage;
